import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithReauth } from './baseQueryWithReauth';
import { setToken } from '../../lib/auth';
import { setCredentials } from '../../store/slices/authSlice';

export interface LoginRequest {
  email: string;
  password: string;
  subdomain?: string;
}

export interface AuthResponse {
  token?: string; // Legacy support
  tokens?: {
    accessToken: string;
    refreshToken: string;
  };
  user: {
    id: string;
    email: string;
    first_name?: string;
    last_name?: string;
    firstName?: string;
    lastName?: string;
    role: string;
    tenantId?: string;
    preferred_catalog_slug?: string;
  };
  riskAnalysis?: any;
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (credentials) => ({
        url: '/auth/login',
        method: 'POST',
        body: credentials,
      }),
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        console.log('[RTK][onQueryStarted] Login mutation started');
        try {
          const { data } = await queryFulfilled;
          console.log('[RTK][onQueryStarted] Login mutation fulfilled with data:', data);
          // Handle both old format {token} and new format {tokens: {accessToken}}
          const token =
            (data as any)?.tokens?.accessToken ??
            (data as any)?.token ??
            (data as any)?.accessToken ??
            (data as any)?.jwt ??
            (data as any)?.data?.token ??
            null;
          console.log('[RTK][onQueryStarted] Extracted token:', !!token);
          if (token) {
            console.log('[RTK][onQueryStarted] Persisting token and updating Redux store');
            const refreshToken = (data as any)?.tokens?.refreshToken ?? null;
            // Update Redux store for immediate availability
            dispatch(setCredentials({ token, user: data.user, refreshToken: refreshToken || undefined }));
            // Also update localStorage/cookie for SSR and persistence
            setToken(token);
            if (refreshToken && typeof window !== 'undefined') {
              window.localStorage.setItem('refreshToken', refreshToken);
            }
          }
        } catch (error) {
          console.log('[RTK][onQueryStarted] Login mutation failed:', error);
          /* noop — handled by callers */
        }
      },
    }),
    register: builder.mutation<AuthResponse, Record<string, unknown>>({
      query: (data) => ({
        url: '/auth/register',
        method: 'POST',
        body: data,
      }),
    }),
    me: builder.query<AuthResponse['user'], void>({
      query: () => ({ url: '/customer/profile' }),
    }),
  }),
});

export const { useLoginMutation, useRegisterMutation, useMeQuery } = authApi;
